import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  ContextoPesquisaApi,
  OrigemPesquisa,
  RegistrarDesfechoPayload,
} from '../types/pesquisaSatisfacaoTypes';
import {
  enfileirarDesfechoPendente,
  lerDesfechosPendentes,
  removerDesfechoPendente,
} from '../utils/desfechoPendente';
import { registrarErroDePesquisa } from '../utils/registrarErroDePesquisa';
import { carregarContexto, registrarDesfecho, registrarExibicao } from './services';

interface ExibirPesquisaArg {
  momentoId: string;
  correlacaoId: string;
  origem: OrigemPesquisa;
}

type FecharPesquisaArg = RegistrarDesfechoPayload;

/**
 * Uma chamada no carregamento do app. Sem ela a régua nasceria vazia e a mesma
 * pesquisa reapareceria a cada F5 — é este thunk que faz o teto valer por pessoa e
 * não por navegador.
 */
export const carregarContextoPesquisa = createAsyncThunk<ContextoPesquisaApi, void>(
  'pesquisaSatisfacao/carregarContexto',
  async () => {
    await drenarDesfechosPendentes();
    return await carregarContexto();
  },
);

/**
 * Reenvia o que a rede engoliu na sessão passada. Roda antes de carregar o contexto
 * para o histórico já vir com o desfecho certo — senão a régua decidiria achando que
 * o cartão daquela vez continua aberto.
 */
async function drenarDesfechosPendentes(): Promise<void> {
  for (const pendente of lerDesfechosPendentes()) {
    try {
      await registrarDesfecho(pendente);
      removerDesfechoPendente(pendente.correlacaoId);
    } catch (erro) {
      // Segue na fila para a próxima vez; falhar aqui não pode impedir o contexto.
      registrarErroDePesquisa('pesquisa.reenvioPendenteFalhou', erro, {
        correlacaoId: pendente.correlacaoId,
      });
    }
  }
}

/**
 * Registra que o cartão apareceu. O `pending` já abre o cartão na tela: esperar a
 * rede para mostrar a pergunta atrasaria o cartão por um registro que só interessa
 * à régua, e a falha aqui não muda nada para quem está trabalhando (RNF-02).
 */
export const exibirPesquisa = createAsyncThunk<void, ExibirPesquisaArg>(
  'pesquisaSatisfacao/exibir',
  async ({ momentoId, correlacaoId, origem }) => {
    try {
      await registrarExibicao({ pontoDeContatoId: momentoId, correlacaoId, origem });
    } catch (erro) {
      registrarErroDePesquisa('pesquisa.exibicaoNaoRegistrada', erro, { correlacaoId });
    }
  },
);

/**
 * Fecha a exibição com o desfecho — a nota, a dispensa ou a expiração.
 *
 * O `pending` é quem muda o cartão de estado, e é isso que cumpre a promessa do
 * design: **a nota está dada no primeiro clique**. Fechar o navegador, recarregar
 * ou desistir na etapa do texto não desfaz nada, porque a UI nunca esperou a
 * resposta do servidor para seguir.
 */
export const fecharPesquisa = createAsyncThunk<void, FecharPesquisaArg>(
  'pesquisaSatisfacao/fechar',
  async (arg) => {
    try {
      await registrarDesfecho(arg);
    } catch (erro) {
      // A UI já afirmou que a nota foi enviada; guardar para a próxima sessão é o que
      // impede a afirmação de virar mentira quando a conexão cai no meio.
      enfileirarDesfechoPendente(arg);
      registrarErroDePesquisa('pesquisa.desfechoNaoRegistrado', erro, {
        correlacaoId: arg.correlacaoId,
        desfecho: arg.desfecho,
      });
    }
  },
);
